/**
 * Created by Ning Jiang on 2021-05-04.
 */

import {IBetWinData, ITombstoneRipData, ITombstoneRipGameData, ITombstoneRipInitData} from "./ITombstoneRipData";
import {IParsedStackedSymbolName, StackedSymbol} from "../../../core/reel/stackedsymbol/StackedSymbol";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {TombstoneRipGameConfig} from "../../TombstoneRipGameConfig";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";

export class TombstoneRipDataParser {

    constructor() {

    }

    public parseReels(rawReels:string[][]):string[][] {
        const result = rawReels.concat();
        this.filterWildInReels(result);
        this.filterReel5(result[4]);
        return this.parseStackedSymbolsInReels(rawReels);
    }

    private filterWildInReels(reels:string[][]):void {
        for(let i:number = 1; i < 4; i++) {
            reels[i].forEach((symName:string, index:number) => {
                if(symName === "W") {
                    reels[i][index] = `W${i}`;
                }
            });
        }
    }

    private filterReel5(reel:string[]):void {
        reel.forEach((sym, symId) => {
            if(sym.includes("M") || sym === "W0") {
                if (!sym.includes("D")){
                    reel[symId] = sym + "D";
                }
            }
        });
    }

    public parseStackedSymbolsInReels(rawReels:string[][], parsedSymbolNamesUnder?:string[]):string[][] {
        let result:string[][] = [];

        rawReels.forEach((rawReel, reelId) => {
            result.push(this.parseStackedSymbolsInReel(rawReel, reelId, parsedSymbolNamesUnder == null ? undefined : parsedSymbolNamesUnder[reelId]));
        });

        return result;
    }

    // Always start to parse the reel from the bottom because in all cases the bottom of the reel is already known.
    // For special cases like the reel is up side down, please create your own parsing.
    public parseStackedSymbolsInReel(rawReel:string[], reelIndex:number, parsedSymbolNameUnder?:string):string[] {
        const input:string[] = rawReel.concat();
        const result:string[] = [];

        if(parsedSymbolNameUnder == null) {
            let bottomStackedSymName:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(input[input.length - 1], reelIndex);
            // Find out if the reel has half stacked symbols in the bottom
            if(bottomStackedSymName.isStacked) {
                let i:number;
                for(i = 1; i < input.length; i++) {
                    const testStackedSymName:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(input[input.length - 1 - i], reelIndex);
                    if(testStackedSymName.isStacked && testStackedSymName.symName === bottomStackedSymName.symName) {
                        bottomStackedSymName = testStackedSymName;
                    } else {
                        break;
                    }
                }
                // i is the first symbol that's not in the stack.
                i = i % bottomStackedSymName.totalNum; // If num of stacked symbols in the bottom is bigger than stacked.totalNum.
                parsedSymbolNameUnder = `${bottomStackedSymName.symName}*${i.toString()}`;
            } else {
                parsedSymbolNameUnder = "NonStackedDummySym";
            }
        }

        while(input.length > 0) {
            const stackedSymNameUnder:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(result.length > 0 ? result[0] : parsedSymbolNameUnder, reelIndex);
            const stackedSymName:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(input.pop()!, reelIndex);

            // For continue stacked symbol
            if(stackedSymNameUnder.isStacked && stackedSymNameUnder.index > 0) {
                if(!stackedSymName.isStacked || stackedSymName.symName !== stackedSymNameUnder.symName) {
                    debugger;
                    Logger.logDev("If you are using the outcome setter, this might be a case that the outcome made an illegal server data. Please take to the server dev to check if that worth a fix.");
                    throw new Error(`ServerDataParser.parseStackedSymbolsInReel():reelId = ${reelIndex}, reelData = ${rawReel.toString()}, symName = ${stackedSymName.symName}, the symbol should be ${stackedSymName.totalNum}-stacked!`);
                }
                result.unshift(`${stackedSymName.symName}*${(stackedSymNameUnder.index - 1).toString()}`);
                continue;
            }

            // For starting a new stacked symbol
            if(stackedSymName.isStacked) {
                result.unshift(`${stackedSymName.symName}*${(stackedSymName.totalNum - 1).toString()}`);
                continue;
            }

            // For non stacked symbol
            result.unshift(stackedSymName.symName);
        }

        return result;
    }

    public parseScatterPositions(rawData:ITombstoneRipData):number[] {
        const result = ArrayHelper.initArrayWithValues(TombstoneRipGameConfig.REELS_NUM, reelId => -1);
        for(let reelId = 1; reelId < 4; reelId++) {
            for(let symId = 1; symId < 4; symId++) {
                if(rawData.reels[reelId][symId] === "S1") {
                    if(result[reelId] === -1) {
                        result[reelId] = symId - 1;
                    } else {
                        // https://github.com/nolimitcity/tombstonerip/issues/393
                        rawData.reels[reelId][symId] = this.findLowNoWinSymbol(rawData.reels[0])
                    }

                }
            }
        }
        if(rawData.reels[4][1] === "S2") {
            result[4] = 0;
        }

        return result;
    }

    private findLowNoWinSymbol(firstReel:string[]):string {
        for(let i = 5; i > 0; i--) {
            const index = firstReel.indexOf(`L${i}`);
            if(index < 1 || index > 2) {
                return `L${i}`;
            }
        }
        // This will never happen becuase reel0 has only 2 symbols.
        return "L5";
    }

    // Game Data:

    public parseSingleWin(playedBetValue:number, betWins:IBetWinData[], xRip:boolean):number {
        if(xRip) {
            return 0;
        }

        if(betWins && betWins.length > 0) {
            let singleWin =  betWins.reduce((a, b:IBetWinData) => {
                return a + b.multipliedAmount;
            }, 0);

            const winCap = playedBetValue * 300000;
            if(singleWin > winCap) {
                return winCap;
            }

            return singleWin;
        }

        return 0;
    }

    public parseNearWinReels(scatterPositions:number[], wasFeatureBuy:boolean):boolean[] {
        const result = ArrayHelper.initArrayWithValues(TombstoneRipGameConfig.REELS_NUM, reelId => false);
        if(wasFeatureBuy) {
            return result;
        }

        if(scatterPositions[1] > -1 && scatterPositions[2] > -1) {
            result[3] = true;
            if(scatterPositions[3] > -1) {
                result[4] = true;
            }
        }

        return result;
    }

    public parseDoReelNudge(rawData:ITombstoneRipGameData):boolean[] {
        const result = ArrayHelper.initArrayWithValues(TombstoneRipGameConfig.REELS_NUM, reelId => false);
        for(let reelId = 1; reelId < 4; reelId++) {
            if(rawData.reels[reelId][1].includes(`W${reelId}`) || rawData.reels[reelId][3].includes(`W${reelId}`)) {
                result[reelId] = true;
            }
        }

        return result;
    }

    public parseConvertScatterToReelSplit(scatterPositions:number[], mode:string):number[] {
        if(mode === GameMode.NORMAL) {
            let counter = 0;
            for(let reelId = 1; reelId < 4; reelId++) {
                if(scatterPositions[reelId] > -1) {
                    counter++;
                }
            }

            if(counter === 2) {
                return [-1, scatterPositions[1], scatterPositions[2], scatterPositions[3], -1];
            }
        }

        return ArrayHelper.initArrayWithValues(TombstoneRipGameConfig.REELS_NUM, reelId => -1);
    }

    public parseReelSplit(rawData:ITombstoneRipGameData, convertScatterToReelSplit:number[]):boolean[] {
        const result = ArrayHelper.initArrayWithValues(TombstoneRipGameConfig.REELS_NUM, reelId => false);
        for(let reelId = 1; reelId < 4; reelId++) {
            for(let symId = 1; symId < 4; symId++) {
                if(rawData.reels[reelId][symId] === "WRS") {
                    result[reelId] = true;
                    break;
                }
            }
        }

        //WRS and S1 can never appear on the same reel.
        convertScatterToReelSplit.forEach((symId, reelId) => {
            if(symId > -1) {
                result[reelId] = true;
            }
        });

        return result;
    }

    public parseWinRatio(winRatio:number):IWinRatioConfig | null {
        let ratioConfigs:IWinRatioConfig[] = TombstoneRipGameConfig.WIN_RATIOS;

        let ratioConfig;
        for(let i:number = ratioConfigs.length - 1; i >= 0; i--) {
            const config = ratioConfigs[i];
            if(winRatio >= config.ratio) {
                ratioConfig = config;
                break;
            }
        }

        if(ratioConfig) {
            return {
                ratio : winRatio,
                name : ratioConfig.name,
                duration : ratioConfig.duration,
                bigWinLevel : ratioConfig.bigWinLevel
            }
        }
        return null;
    }
}